import { useMemo } from "react"

import { api } from "@/api"

import { shortlistListingSchema } from "./schemas"
import type { ShortlistsState, ShortlistSummary } from "./types"

export const SHORTLISTS_URL = "/search/api/v1/shortlists"

// Search owns the listing endpoint and injects it into the application's
// RTK Query api (ADR 0004), so the module fetches the way every other JSON
// API is fetched and shares the shell's caching and deduplication. A body
// that fails the listing schema rejects the query, which the hook reports
// as its error state.
const shortlistsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getShortlists: builder.query<ShortlistSummary[], void>({
      query: () => ({
        url: SHORTLISTS_URL,
        headers: { Accept: "application/json" },
      }),
      transformResponse: (response: unknown) =>
        shortlistListingSchema.parse(response).map((row) => ({
          id: row.id,
          name: row.name,
          classCount: row.class_count,
          educatorCount: row.educator_count,
        })),
    }),
  }),
})

const { useGetShortlistsQuery } = shortlistsApi

/**
 * The principal's reachable shortlists, fetched through the application's
 * API provider with the browser session. The consumer presents each status
 * itself.
 */
export function useShortlists(): ShortlistsState {
  const { data, isError } = useGetShortlistsQuery()

  return useMemo((): ShortlistsState => {
    if (isError) return { status: "error" }
    if (data === undefined) return { status: "loading" }
    return { status: "ready", shortlists: data }
  }, [data, isError])
}
